import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { EntitlementsProvider } from "@/context/EntitlementsContext";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import Profile from "@/pages/Profile";
import Resume from "@/pages/Resume";
import Chat from "@/pages/Chat";
import Salary from "@/pages/Salary";
import Roadmap from "@/pages/Roadmap";
import Jobs from "@/pages/Jobs";
import Insights from "@/pages/Insights";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Certifications from "@/pages/Certifications";
import Achievements from "@/pages/Achievements";
import Network from "@/pages/Network";
import Interview from "@/pages/Interview";
import LiveInterview from "@/pages/LiveInterview";
import InterviewHistory from "@/pages/InterviewHistory";
import InterviewSessionDetail from "@/pages/InterviewSessionDetail";
import Negotiation from "@/pages/Negotiation";
import Notifications from "@/pages/Notifications";
import AuthCallback from "@/pages/AuthCallback";
import Financial from "@/pages/Financial";
import Executive from "@/pages/Executive";
import Settings from "@/pages/Settings";
import PublicProfile from "@/pages/PublicProfile";
import Referrals from "@/pages/Referrals";
import Admin from "@/pages/Admin";
import Directory from "@/pages/Directory";
import Assessments from "@/pages/Assessments";
import AssessmentRun from "@/pages/AssessmentRun";
import Portfolio from "@/pages/Portfolio";
import PublicPortfolio from "@/pages/PublicPortfolio";
import Recruiter from "@/pages/Recruiter";
import JobsBoard from "@/pages/JobsBoard";
import Support from "@/pages/Support";
import AdminSupport from "@/pages/AdminSupport";
import AdminEmail from "@/pages/AdminEmail";
import Unsubscribe from "@/pages/Unsubscribe";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import Privacy from "@/pages/legal/Privacy";
import Terms from "@/pages/legal/Terms";
import CookiePolicy from "@/pages/legal/CookiePolicy";
import AIUsagePolicy from "@/pages/legal/AIUsagePolicy";
import SubscriptionPolicy from "@/pages/legal/SubscriptionPolicy";
import RefundPolicy from "@/pages/legal/RefundPolicy";
import AcceptableUse from "@/pages/legal/AcceptableUse";
import DataRetention from "@/pages/legal/DataRetention";
import Accessibility from "@/pages/legal/Accessibility";
import SecurityPolicy from "@/pages/legal/SecurityPolicy";
import CopyrightPolicy from "@/pages/legal/Copyright";
import Disclaimer from "@/pages/legal/Disclaimer";
import TitleManager from "@/components/TitleManager";
import ErrorBoundary from "@/components/ErrorBoundary";
import { CookieConsentProvider } from "@/components/legal/cookieConsent";
import { AnalyticsProvider } from "@/components/AnalyticsProvider";
import { CookieBanner, CookiePreferencesModal } from "@/components/legal/CookieConsentUI";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading)
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <div className="animate-pulse font-mono text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  // Email-verification gate — if the user hasn't confirmed their email, route
  // them to the verification screen instead of letting them into the app.
  // Backend enforcement still applies (every protected API returns 403
  // EMAIL_NOT_VERIFIED), so this is a UX layer not a security layer.
  if (user.email_verified === false && !location.pathname.startsWith("/verify-email")) {
    return <Navigate to="/verify-email" replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <TitleManager />
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/u/:username" element={<PublicProfile />} />
      <Route path="/p/:username" element={<PublicPortfolio />} />
      {/* Directory is temporarily hidden from public UX because the platform
          does not yet have enough real user profiles. Backend + component
          are preserved; the route redirects to the dashboard (or landing
          for unauthenticated visitors) so old links don't 404. */}
      <Route path="/directory" element={<Navigate to="/app" replace />} />
      <Route path="/jobs-board" element={<JobsBoard />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route path="/ai-policy" element={<AIUsagePolicy />} />
      <Route path="/subscription-policy" element={<SubscriptionPolicy />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/acceptable-use" element={<AcceptableUse />} />
      <Route path="/data-retention" element={<DataRetention />} />
      <Route path="/accessibility" element={<Accessibility />} />
      <Route path="/security" element={<SecurityPolicy />} />
      <Route path="/copyright" element={<CopyrightPolicy />} />
      <Route path="/disclaimer" element={<Disclaimer />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/app" element={<Protected><Layout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="profile" element={<Profile />} />
        <Route path="resume" element={<Resume />} />
        <Route path="chat" element={<Chat />} />
        <Route path="salary" element={<Salary />} />
        <Route path="roadmap" element={<Roadmap />} />
        <Route path="jobs" element={<Jobs />} />
        <Route path="network" element={<Network />} />
        <Route path="interview" element={<Interview />} />
        <Route path="negotiation" element={<Negotiation />} />
        <Route path="financial" element={<Financial />} />
        <Route path="executive" element={<Executive />} />
        <Route path="insights" element={<Insights />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="certifications" element={<Certifications />} />
        <Route path="achievements" element={<Achievements />} />
        <Route path="referrals" element={<Referrals />} />
        <Route path="admin" element={<Admin />} />
        <Route path="admin/support" element={<AdminSupport />} />
        <Route path="admin/email" element={<AdminEmail />} />
        <Route path="support" element={<Support />} />
        <Route path="assessments" element={<Assessments />} />
        <Route path="assessments/:type" element={<AssessmentRun />} />
        <Route path="assessments/:type/:mode" element={<AssessmentRun />} />
        <Route path="interview/live" element={<LiveInterview />} />
        <Route path="interview/history" element={<InterviewHistory />} />
        <Route path="interview/session/:id" element={<InterviewSessionDetail />} />
        <Route path="portfolio" element={<Portfolio />} />
        <Route path="recruiter" element={<Recruiter />} />
        <Route path="settings" element={<Settings />} />
        <Route path="pricing" element={<Pricing />} />
      </Route>
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="dark">
        <AuthProvider>
          <CurrencyProvider>
            <EntitlementsProvider>
              <CookieConsentProvider>
                <BrowserRouter>
                  <AnalyticsProvider />
                  <Toaster
                    position="bottom-right"
                    closeButton
                    richColors
                    toastOptions={{ className: "font-body" }}
                  />
                  <AppRouter />
                  <CookieBanner />
                  <CookiePreferencesModal />
                </BrowserRouter>
              </CookieConsentProvider>
            </EntitlementsProvider>
          </CurrencyProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
